import React, { useMemo } from 'react';
import type { TripResponseArgs } from '@this/domain/trip/trip2';
import { useAITTheme, type AITTheme } from '@this/shared/ui/theme';
import { first } from '@this/src/util';

interface Props {
  trip: TripResponseArgs;
  isEditDisabled: boolean;
  canEdit: boolean;
  onEditClick: () => void;
}

const EMPTY_VALUE = '未記入';

const buildTripOverview = (trip: TripResponseArgs) => {
  const destination = trip.final_destination || EMPTY_VALUE;
  const purpose = trip.purpose || EMPTY_VALUE;
  const internalNumber = trip.internal_number || EMPTY_VALUE;
  const departmentName = first(trip.charging_departments || [])?.name;
  const projectName = first(trip.projects || [])?.name;

  return {
    destination,
    purpose,
    internalNumber,
    department: departmentName || EMPTY_VALUE,
    project: projectName || EMPTY_VALUE
  };
};

const TripApplicationInfoSection = ({ trip, isEditDisabled, canEdit, onEditClick }: Props) => {
  const { theme } = useAITTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);
  const tripOverview = buildTripOverview(trip);

  const isDisabled = isEditDisabled || !canEdit;

  return (
    <>
      <div style={styles.header}>
        <h3 style={styles.title}>出張申請に関する情報</h3>
        <button
          type="button"
          style={{
            ...styles.linkButton,
            ...(isDisabled ? styles.linkButtonDisabled : {})
          }}
          onClick={onEditClick}
          disabled={isDisabled}
        >
          編集
        </button>
      </div>
      <ul style={styles.list}>
        <li>出張先: {tripOverview.destination}</li>
        <li>出張の目的: {tripOverview.purpose}</li>
        <li>社内管理番号: {tripOverview.internalNumber}</li>
        <li>費用負担部署: {tripOverview.department}</li>
        <li>プロジェクト: {tripOverview.project}</li>
      </ul>
    </>
  );
};

const getStyles = (theme: AITTheme) => ({
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  title: {
    fontSize: '14px',
    fontWeight: 600 as const,
    margin: 0
  },
  list: {
    listStyle: 'none' as const,
    padding: 0,
    margin: '12px 0 0',
    lineHeight: 1.8,
    fontSize: '13px'
  },
  linkButton: {
    border: 'none',
    background: 'none',
    color: theme.linkColor,
    cursor: 'pointer'
  },
  linkButtonDisabled: {
    color: theme.grayTextColor || '#9ca3af',
    cursor: 'not-allowed',
    opacity: 0.5
  }
});

export default TripApplicationInfoSection;
